import { z } from 'zod';
import type { AdminPermission } from './permissions';

/**
 * Email-code lockout and admin unlock (owner decision 2026-10-09 #5).
 *
 * The Keycloak extension locks an address after 10 wrong email codes in a rolling 24 h
 * (FailureLimiter). "Unlock email sign-in" in Admin → Users clears that lock early.
 */

/** GET/DELETE: read or clear one user's email-code lock. */
export const EMAIL_CODE_LOCK_ROUTE_PATH = '/api/v1/admin/users/:userId/email-code-lock';

/** The permission that already governs changing a user (roles, disable). */
export const EMAIL_CODE_LOCK_PERMISSION = 'users.assign_roles' satisfies AdminPermission;

/** audit_log action for a successful unlock. */
export const EMAIL_CODE_LOCK_AUDIT_ACTION = 'users.email_code.unlock';

/** GET response. `unlocksAt` is set only while locked: when the lock ends on its own. */
export const emailCodeLockSchema = z.object({
  locked: z.boolean(),
  /** Wrong codes for the user's address in the current window. */
  failures: z.number().int().min(0),
  unlocksAt: z.iso.datetime().optional(),
});
export type EmailCodeLock = z.infer<typeof emailCodeLockSchema>;

export interface EmailCodeLockActor {
  roleIds: readonly string[];
  permissions: ReadonlySet<string>;
  highestRank: number;
}

/**
 * Who may read or clear a user's email-code lock:
 * - a Tenant Admin: anyone, including other admins and other Tenant Admins;
 * - anyone else holding the users permission: only users whose highest role ranks below theirs.
 * The server enforces this; the app uses it only to hide the button.
 */
export function canManageEmailCodeLock(
  actor: EmailCodeLockActor,
  targetHighestRank: number,
): boolean {
  if (!actor.permissions.has(EMAIL_CODE_LOCK_PERMISSION)) return false;
  if (actor.roleIds.includes('tenant_admin')) return true;
  return targetHighestRank < actor.highestRank;
}
